/**
 * AppRoutes — definição centralizada de todas as rotas da aplicação.
 * Importado por App.tsx como único ponto de composição de rotas.
 *
 * Code splitting: todas as páginas são carregadas sob demanda via React.lazy.
 * O PageLoader é exibido enquanto o chunk ainda não foi baixado.
 */

import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import DefaultPage        from "../components/default-page/default-page";
import { AppFrameRoute } from "./AppFrameRoute";
import { ProtectedRoute } from "./ProtectedRoute";
import { OnboardingGuard } from "./OnboardingGuard";
import { EmailVerificationGate } from "./EmailVerificationGate";
import { BetaAccessGate } from "./BetaAccessGate";
// A `/` decide entre landing e app consultando o veredito do beta — ver
// RootEntry.tsx (BUG-93).
import { RootEntry } from "./RootEntry";
import { PublicOrGuarded } from "./PublicOrGuarded";
import { AnonymousOrGuarded } from "./AnonymousOrGuarded";
import { PageLoader } from "./PageLoader";
import { adminRoutes } from "./adminRoutes";
import { ScrollToTop }    from "../components/layout/ScrollToTop";
import { OverlayHistoryJanitor } from "../components/layout/OverlayHistoryJanitor";
import { ErrorBoundary }  from "../components/layout/ErrorBoundary";
import { useBadgeAutoUnlock } from "../features/gamification/hooks/useBadgeAutoUnlock";
import { useBadgeAnnounceWatcher } from "../features/gamification/hooks/useBadgeAnnounceWatcher";
import { useReferralCapture } from "../features/affiliates/hooks/useReferralCapture";
import { useDragScroll } from "../lib/interaction/useDragScroll";
import { CoinDropProvider } from "../components/domain/CoinDropProvider";
import { FocusSessionProvider } from "../features/focus/context/FocusSessionProvider";
import { isPurchaseAvailable } from "../features/pro/purchaseAvailability";

/**
 * Avaliado UMA vez, na carga do módulo. A plataforma não muda no meio da
 * sessão, e uma constante mantém a tabela de rotas estável entre renders — um
 * `Route` que trocasse de `element` remontaria a página inteira.
 */
const PURCHASE_AVAILABLE = isPurchaseAvailable();

// ── Lazy pages ──────────────────────────────────────────────────────────────
// A casca institucional era import ESTÁTICO e arrastava para o entry os dois
// scripts de conteúdo assado (`scripts/public-pages-content.mjs` +
// `landing-seo-content.mjs`, ~18 KB minificados) — texto de /sobre, /ajuda e
// /baixar que todo boot da Home baixava sem usar. Lazy como as páginas que ela
// emoldura (17/09, rodada de performance).
const InstitutionalLayout = lazy(() =>
  import("../layouts/InstitutionalLayout").then((m) => ({ default: m.InstitutionalLayout })),
);
const Login        = lazy(() => import("../pages/Login"));
const Home         = lazy(() => import("../pages/Home"));
const Streak       = lazy(() => import("../pages/Streak"));
const Onboarding   = lazy(() => import("../features/onboarding/Onboarding"));
const Search       = lazy(() => import("../pages/Search"));
const Profile      = lazy(() => import("../pages/Profile"));
const PublicProfile = lazy(() => import("../pages/PublicProfile"));
const Match        = lazy(() => import("../pages/Match"));
const Author       = lazy(() => import("../pages/Author"));
const AuthorsHub   = lazy(() => import("../pages/AuthorsHub"));
const AuthorDashboard = lazy(() => import("../pages/AuthorDashboard"));
const Publisher    = lazy(() => import("../pages/Publisher"));
const BookDetail   = lazy(() => import("../pages/BookDetail"));
const Shelf        = lazy(() => import("../pages/Shelf"));
const PublicShelf  = lazy(() => import("../pages/PublicShelf"));
const PublicReadingPage = lazy(() => import("../pages/PublicReadingPage"));
const Badges       = lazy(() => import("../features/gamification/Badges"));
const Settings     = lazy(() => import("../pages/Settings"));
const Lists        = lazy(() => import("../pages/Lists"));
const ListDetail   = lazy(() => import("../pages/ListDetail"));
const ProSubscription = lazy(() => import("../pages/ProSubscription"));
const Subscription    = lazy(() => import("../pages/Subscription"));
const CheckoutSuccess = lazy(() => import("../pages/CheckoutSuccess"));
const CheckoutCanceled = lazy(() => import("../pages/CheckoutCanceled"));
const WalletHistoryPage = lazy(() => import("../features/economy/pages/WalletHistoryPage"));
const StorePage       = lazy(() => import("../features/store/pages/StorePage"));
const LeaguesPage     = lazy(() => import("../features/leagues/pages/LeaguesPage"));
const HallOfChampionsPage = lazy(() => import("../features/leagues/pages/HallOfChampionsPage"));
const PrivateLeaguePage = lazy(() => import("../features/leagues/pages/PrivateLeaguePage"));
const SobrePage       = lazy(() => import("../pages/institutional/SobrePage"));
const LivrosPage      = lazy(() => import("../pages/LivrosPage"));
const ParaAutores     = lazy(() => import("../pages/ParaAutores"));
const AjudaPage       = lazy(() => import("../pages/institutional/AjudaPage"));
const BaixarPage      = lazy(() => import("../pages/institutional/BaixarPage"));
const PrivacyPage     = lazy(() => import("../pages/institutional/PrivacyPage"));
const TermsPage       = lazy(() => import("../pages/institutional/TermsPage"));
const AiDiscover      = lazy(() => import("../pages/AiDiscover"));
const Discover        = lazy(() => import("../pages/Discover"));
const CreatorDashboard = lazy(() => import("../pages/CreatorDashboard"));
const FounderLanding  = lazy(() => import("../pages/FounderLanding"));
const Insights        = lazy(() => import("../pages/Insights"));

/**
 * Build das lojas (`build:mobile`). Constante de COMPILAÇÃO: o Vite troca o
 * `import.meta.env` por literal e o Rollup elimina o ramo morto — é assim que o
 * painel `/admin` (só web) fica fora do app. Ver `adminRoutes.tsx`.
 */
const IS_NATIVE_BUILD = import.meta.env.VITE_NATIVE_BUILD === "true";

// ── Helpers de rota ─────────────────────────────────────────────────────────
/**
 * `/ajuda/<tema>` → `/ajuda#<tema>`.
 *
 * Não existe rota por pergunta (a `/ajuda` é uma página só, com âncoras — é o
 * formato que o `FAQPage` do schema.org descreve). Mas é exatamente esse
 * endereço que um modelo de linguagem inventa ao citar um tópico, e sem rota o
 * app renderiza tela BRANCA. Aqui o palpite aterrissa na pergunta certa em vez
 * de no vazio; se o tema não existir, a âncora não casa e a pessoa fica no
 * topo da página, que é o pior caso aceitável.
 */
function HelpTopicRedirect() {
  const { slug } = useParams();
  return <Navigate to={slug ? `/ajuda#${slug}` : "/ajuda"} replace />;
}

// ── /billing → /pro redirect preserving query params ─────────────────────────
function BillingRedirect() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={qs ? `/pro?${qs}` : "/pro"} replace />;
}

// ── /insights → /estatisticas preservando a query (Estatísticas v2, 26/09) ────
// O push do digest abre `/insights?month=YYYY-MM` — e continua abrindo: os apps
// já instalados das lojas só conhecem essa rota. Perder o `?month` jogaria a
// pessoa num mês diferente do que a notificação prometeu.
function StatsRedirect() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={qs ? `/estatisticas?${qs}` : "/estatisticas"} replace />;
}

// ── Guard helper ─────────────────────────────────────────────────────────────
function Guarded({ children }: { children: React.ReactElement }) {
  return (
    <ProtectedRoute>
      {/* BetaAccessGate vem antes do onboarding: quem está fora da allowlist
          não deve nem começar a onboardar. Com o gate OFF é passthrough. */}
      <BetaAccessGate>
        <OnboardingGuard>{children}</OnboardingGuard>
      </BetaAccessGate>
    </ProtectedRoute>
  );
}

// ── Routes ───────────────────────────────────────────────────────────────────
export function AppRoutes() {
  useBadgeAutoUnlock();
  // BUG-281 — anuncia o que o SERVIDOR concedeu (trigger em `user_stats`),
  // sem depender de a callable ter sido chamada.
  useBadgeAnnounceWatcher();
  // Captura `?ref=` mesmo deslogado e aplica quando houver sessão — o
  // visitante que clica no link do creator ainda não tem conta.
  useReferralCapture();
  // Arraste de mouse nos carrosséis. Fica NESTE nível, e não no `AppFrame`,
  // porque há rail horizontal fora do frame também (`/admin`, landing) e o
  // gesto não tem por que existir só em metade do produto.
  useDragScroll();

  return (
    <ErrorBoundary>
      <ScrollToTop />
      {/* Entradas de histórico de modal sem dono (voltar "morto") — 24/09. */}
      <OverlayHistoryJanitor />
      {/* Os watchers de celebração (liga, badge, convite de push) vivem DENTRO
          do `AppFrame`, não aqui: dois deles desenham com `fixed` cru sem
          portal e, montados neste nível, escapariam do frame no desktop. A
          allowlist de celebração só contém rotas emolduradas, então nenhum
          gatilho se perde. O `CoinDropProvider` fica: ele usa portal, que já
          aterrissa dentro do frame, e assim mantém alcance global. */}
      <CoinDropProvider />
      {/* A sessão de Foco é do APP, não da página que a iniciou (BUG-164). O
          provider fica ACIMA das rotas para sobreviver à navegação; quem
          RENDERIZA o modal é o `FocusSessionHost`, dentro do `AppFrame` —
          `FocusModeModal` desenha com `fixed` cru sem portal e escaparia do
          frame se montasse neste nível, como os watchers de celebração. */}
      <FocusSessionProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Institucionais: a casca é consciente de sessão. Estas rotas são
              públicas, mas o público delas NÃO é só quem está deslogado —
              Ajustes › Privacidade linka os dois documentos de dentro do app. */}
          <Route element={<InstitutionalLayout />}>
            {/* `/sobre`, `/ajuda` e `/baixar` são PRÉ-RENDERIZADAS no build
                (`scripts/prerender-pages.mjs` + rewrite no `firebase.json`):
                o crawler — e a camada de IA em cima dele — recebe o conteúdo
                no HTML, sem depender de o React montar. Estes componentes são
                o que a pessoa vê depois da hidratação, e leem exatamente a
                mesma fonte de texto que o script assa. */}
            <Route path="/sobre"   element={<SobrePage />} />
            <Route path="/ajuda"   element={<AjudaPage />} />
            <Route path="/baixar"  element={<BaixarPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms"   element={<TermsPage />} />
          </Route>

          {/* Apelidos de URL. Existem porque o app NÃO tem catch-all: uma rota
              que não casa renderiza tela BRANCA, não 404 (mesma razão que
              manteve `/wrap` de pé como redirect). Então todo endereço que a
              gente publica — em llms.txt, em rede social, num QR — precisa
              existir aqui.

              `/about` é redirect e não uma segunda página: ela e a `/sobre`
              seriam dois documentos públicos com o mesmo título e o mesmo
              assunto, e conteúdo duplicado faz o Google escolher um dos dois
              arbitrariamente. O conteúdo da antiga `/about` está inteiro na
              `/sobre`. O `firebase.json` faz o 301 equivalente para quem
              chega de fora — este aqui cobre a navegação interna. */}
          <Route path="/about"       element={<Navigate to="/sobre" replace />} />
          <Route path="/termos"      element={<Navigate to="/terms" replace />} />
          <Route path="/privacidade" element={<Navigate to="/privacy" replace />} />
          <Route path="/ajuda/:slug" element={<HelpTopicRedirect />} />

          {/* ── Largura plena ──────────────────────────────────────────────
              As páginas que NÃO entram no frame de mobile. O critério é o
              público, não a rota: são as superfícies de aquisição e de entrada,
              que ou já têm desenho de desktop (a landing tem breakpoints
              próprios até 1440px) ou são a primeira coisa que alguém vê vindo
              de um anúncio — espremer isso numa coluna de 430px seria trocar a
              versão desenhada por uma que ninguém desenhou.

              `/pro` está aqui como funil (link de anúncio/bio/QR, registrado
              abaixo); `/pro/sucesso` e `/pro/checkout-cancelado` NÃO estão —
              são o retorno do Stripe para quem já está dentro do app. */}
          <Route element={<DefaultPage />}>
            <Route path="/"        element={<RootEntry />} />
            <Route path="/login"   element={<Login />} />
            {/* Funis de compra. No build NATIVO as duas somem e viram redirect
                para a Home — ver `features/pro/purchaseAvailability.ts`. É a
                camada 2 das três: mesmo que um botão de upsell escape em
                alguma tela, ele não tem para onde levar. Redirect, e não
                ausência de rota: sem rota o app não tem catch-all e a URL
                renderizaria tela BRANCA (mesma razão registrada no `/wrap`). */}
            <Route
              path="/founder"
              element={PURCHASE_AVAILABLE ? <FounderLanding /> : <Navigate to="/home" replace />}
            />
            <Route
              path="/pro"
              element={
                PURCHASE_AVAILABLE
                  ? <Guarded><ProSubscription /></Guarded>
                  : <Navigate to="/home" replace />
              }
            />
            {/* Hub de fichas (14/09): `/livros` e `/livros/:page` são servidas
                pela `renderBookPage` com a lista embutida; sem o payload
                (navegação client-side) a página busca a própria URL. Pública,
                sem guard, igual à própria `/` — ver `LivrosPage.tsx`. */}
            <Route path="/livros" element={<LivrosPage />} />
            <Route path="/livros/:page" element={<LivrosPage />} />
            {/* Página para autores (Livra Autores A1a, D1/D17): link da bio e
                dos posts. Pública e estática; o "Sou autor" leva ao login e
                volta com `?abrir=1` (`SHAREABLE_TARGET`). */}
            <Route path="/para-autores" element={<ParaAutores />} />
          </Route>

          {/* ── Frame de mobile ────────────────────────────────────────────
              O app inteiro, ficha de livro incluída, num grupo SÓ: o
              `KeepAliveOutlet` do `AppFrame` guarda as telas deste grupo, e
              só o que está aqui pode ser guardado e voltar pronto (BUG-331).
              Ver `layouts/AppFrame.tsx` para por que a contenção é uma casca
              com scroller interno e não um `max-width`. */}
          <Route element={<AppFrameRoute />}>
            {/* ── Ficha de livro: casca decidida pela SESSÃO ───────────────
                As duas rotas de livro são `PublicOrGuarded` desde 06/08: são
                a abertura de SEO orgânico. Sem sessão, o BookDetail vira a
                versão SITE (`PublicBookPage`) e toda ação vai pro `/login`
                (`useRequireAuth`); com sessão, a cadeia de guards é a mesma de
                sempre, inclusive o gate do beta, que é o buraco que
                `PublicOrGuarded` fecha. O `AppFrameRoute` (05/09) reconhece
                estas duas rotas e escolhe a casca com a MESMA regra: visitante
                com payload = largura plena; qualquer outro caso = o `AppFrame`. */}
            <Route path="/book/:id"          element={<PublicOrGuarded><BookDetail /></PublicOrGuarded>} />
            {/* URL canônica SEO-friendly: /livro/{workSlug} onde workSlug = workId sem prefixo `work:`. */}
            <Route path="/livro/:workSlug"   element={<PublicOrGuarded><BookDetail /></PublicOrGuarded>} />

            <Route
              path="/onboarding"
              /* EmailVerificationGate vem DEPOIS do beta e ANTES do wizard: a
                 rule `hasVerifiedEmail()` barra o fecho do onboarding, então
                 descobrir isso no último passo — com tema, gêneros e favoritos
                 já escolhidos — seria o pior lugar possível. */
              element={<ProtectedRoute><BetaAccessGate><EmailVerificationGate><Onboarding /></EmailVerificationGate></BetaAccessGate></ProtectedRoute>}
            />

            <Route path="/home"              element={<Guarded><Home /></Guarded>} />
            <Route path="/discover"          element={<Guarded><Discover /></Guarded>} />
            <Route path="/ai"                element={<Guarded><AiDiscover /></Guarded>} />
            <Route path="/streak"            element={<Guarded><Streak /></Guarded>} />
            {/* A tela do mascote (coruja em emoji do sistema) foi substituída
                pela Ofensiva em 13/08. `/mascot` redireciona em vez de sumir:
                era o destino do toque na chama da Home desde sempre, e pode
                estar no histórico de qualquer aba aberta. */}
            <Route path="/mascot"            element={<Navigate to="/streak" replace />} />
            <Route path="/search"            element={<Guarded><Search /></Guarded>} />
            <Route path="/profile"           element={<Guarded><Profile /></Guarded>} />
            {/* F1.5 do Roadmap PRO (11/09): visitante sem conta vê o MESMO
                perfil (`AnonymousOrGuarded` abre uma sessão anônima do
                Firebase — nunca abre as Firestore Rules). Quem já tem conta
                segue pela cadeia normal de `Guarded`, sem mudança. */}
            <Route path="/profile/:username" element={<AnonymousOrGuarded><PublicProfile /></AnonymousOrGuarded>} />
            {/* Estante pública — read-only. Fica sob `/profile/:username` de
                propósito: é a mesma família de URL do perfil público, e não uma
                segunda (`/u/...`) apontando pra mesma pessoa. */}
            <Route path="/profile/:username/shelf/:status" element={<AnonymousOrGuarded><PublicShelf /></AnonymousOrGuarded>} />
            {/* F4 do Roadmap PRO (12/09): vitrine "link na bio" da leitura
                atual, PRO-exclusiva do DONO — não do visitante. Mesmo guard
                do perfil público: o dado (estante, resenha, notas isPublic)
                já é anônimo-legível pelas Rules de hoje. Ver
                docs/systems/lendo-page.md §3.1. */}
            <Route path="/lendo/:username"   element={<AnonymousOrGuarded><PublicReadingPage /></AnonymousOrGuarded>} />
            <Route path="/match/:username"   element={<Guarded><Match /></Guarded>} />
            <Route path="/author/:authorId"  element={<Guarded><Author /></Guarded>} />
            {/* Livra Autores A2: hub com 4 abas (`?aba=`) e página da editora (5+ obras). */}
            {/* Painel de autoria do dono (D59/D60, docs/systems/author-dashboard.md): a porta é "Meus livros". */}
            <Route path="/autores/painel"    element={<Guarded><AuthorDashboard /></Guarded>} />
            <Route path="/autores"           element={<Guarded><AuthorsHub /></Guarded>} />
            <Route path="/editora/:publisherId" element={<Guarded><Publisher /></Guarded>} />
            <Route path="/shelf/:status"     element={<Guarded><Shelf /></Guarded>} />
            <Route path="/badges"            element={<Guarded><Badges /></Guarded>} />
            <Route path="/settings"          element={<Guarded><Settings /></Guarded>} />
            <Route path="/lists"             element={<Guarded><Lists /></Guarded>} />
            <Route path="/list/:id"          element={<Guarded><ListDetail /></Guarded>} />
            {/* Painel de quem já paga. Rota própria desde 03/08: `/pro` é o
                funil (link de anúncio/bio/QR) e não pode ser também a tela de
                conta. As duas se redirecionam por tier — ver Subscription.tsx. */}
            <Route path="/assinatura"         element={<Guarded><Subscription /></Guarded>} />
            {/* Retorno do Stripe. Guarded porque o checkout exige auth — quem
                chega aqui já estava logado quando comprou. No nativo não há
                checkout, então não há retorno: as duas viram redirect junto
                com `/pro`. Deixá-las de pé mostraria "compra concluída" (ou o
                CTA "tentar de novo" da tela de cancelado, que leva a `/pro`)
                num app onde comprar não existe. */}
            <Route
              path="/pro/sucesso"
              element={
                PURCHASE_AVAILABLE
                  ? <Guarded><CheckoutSuccess /></Guarded>
                  : <Navigate to="/home" replace />
              }
            />
            <Route
              path="/pro/checkout-cancelado"
              element={
                PURCHASE_AVAILABLE
                  ? <Guarded><CheckoutCanceled /></Guarded>
                  : <Navigate to="/home" replace />
              }
            />
            <Route path="/billing"            element={<BillingRedirect />} />
            {/* Wrap anual REMOVIDO em 05/08 (decisão do fundador): seguia na
                gramática visual antiga e sem as mecânicas que o produto quer.
                Vira redirect e não sumiço: a página montava o próprio link
                compartilhável com `origin` (`/wrap/2026`), e sem rota o app
                não tem catch-all — a URL renderizaria tela BRANCA. As
                Estatísticas (26/09) são o hub de retrospectiva, e o Wrap novo
                mora na aba Ano delas. */}
            <Route path="/wrap/:year"         element={<Navigate to="/estatisticas" replace />} />
            <Route path="/wrap"               element={<Navigate to="/estatisticas" replace />} />
            {/* /wallet legado → redirect para /store (Sprint G §4.1). */}
            <Route path="/wallet"            element={<Navigate to="/store" replace />} />
            <Route path="/wallet/history"    element={<Guarded><WalletHistoryPage /></Guarded>} />
            <Route path="/store"             element={<Guarded><StorePage /></Guarded>} />
            <Route path="/leagues"           element={<Guarded><LeaguesPage /></Guarded>} />
            {/* Estática: o React Router a ranqueia acima de `/leagues/:roomId` — "hall" nunca vira id de sala. */}
            <Route path="/leagues/hall"      element={<Guarded><HallOfChampionsPage /></Guarded>} />
            <Route path="/leagues/:roomId"   element={<Guarded><PrivateLeaguePage /></Guarded>} />
            <Route path="/creator/dashboard" element={<Guarded><CreatorDashboard /></Guarded>} />
            <Route path="/estatisticas"     element={<Guarded><Insights /></Guarded>} />
            <Route path="/insights"         element={<StatsRedirect />} />
          </Route>

          {/* Admin — só na build web: ver adminRoutes.tsx. */}
          {IS_NATIVE_BUILD ? null : adminRoutes()}
        </Routes>
      </Suspense>
      </FocusSessionProvider>
    </ErrorBoundary>
  );
}
